import {
  displayUserName,
  isDeletedUserEmail,
} from "@modules/user/deletedUser";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { Badge } from "~/components/ui/badge";
import { CopyButton } from "~/components/ui/copy-button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Progress } from "~/components/ui/progress";
import { cn } from "~/lib/utils";
import { calculateDammChecksum } from "~/util/Damm";
import { Routes, getRoute } from "~/routes";
import { useCassettesQuery } from "generated/hooks";

type CassetteSortKey =
  | "lastUsedAt"
  | "remainingCustomerTests"
  | "remainingPhysicalSensors";
type CassetteSortDirection = "ASC" | "DESC";

interface CassetteWithSerialNumber {
  id: string;
  serialNumber?: {
    serialNumber: number;
    range?: { productType?: { code?: string | null } | null } | null;
  } | null;
}

/** The printed serial number, or null when none has been assigned. */
const formatCassetteSerialNumber = (
  cassette: CassetteWithSerialNumber
): string | null => {
  const serialNumber = cassette.serialNumber?.serialNumber;

  if (serialNumber === null || serialNumber === undefined) {
    return null;
  }

  const productCode = cassette.serialNumber?.range?.productType?.code ?? "";
  const full = `${productCode}${serialNumber.toString().padStart(7, "0")}`;

  return `${full}${calculateDammChecksum(full)}`;
};

const formatCassetteLabel = (cassette: CassetteWithSerialNumber) =>
  formatCassetteSerialNumber(cassette) ?? `Cassette ${cassette.id.slice(0, 8)}`;

const getRatio = (used: number, total: number) => {
  if (total <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, (used / total) * 100));
};

const getYieldTone = (yieldPct: number | null) => {
  if (yieldPct === null) {
    return "text-muted-foreground";
  }

  if (yieldPct > 90) {
    return "text-emerald-600 dark:text-emerald-400";
  }

  if (yieldPct >= 80) {
    return "text-amber-600 dark:text-amber-400";
  }

  return "text-rose-600 dark:text-rose-400";
};

interface UsageMeterCellProps {
  remaining: number;
  used: number;
  total: number;
}

const UsageMeterCell: React.FC<UsageMeterCellProps> = ({
  remaining,
  used,
  total,
}) => (
  <div className="min-w-[140px] space-y-1.5">
    <div className="flex items-baseline gap-2">
      <div className="flex items-baseline gap-2">
        <span className="text-base font-semibold tabular-nums">
          {remaining}
        </span>
        <span className="text-xs text-muted-foreground">remaining</span>
      </div>
    </div>
    <Progress value={getRatio(used, total)} className="h-1" />
  </div>
);

interface YieldCellProps {
  good: number;
  bad: number;
}

const YieldCell: React.FC<YieldCellProps> = ({ good, bad }) => {
  const checked = good + bad;
  const yieldPct = checked > 0 ? (good / checked) * 100 : null;
  const goodShare = checked > 0 ? (good / checked) * 100 : 0;
  const badShare = checked > 0 ? (bad / checked) * 100 : 0;

  return (
    <div className="min-w-[150px] space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span
          className={cn(
            "text-base font-semibold tabular-nums",
            getYieldTone(yieldPct)
          )}
        >
          {yieldPct === null ? "—" : `${yieldPct.toFixed(1)}%`}
        </span>
        <span className="text-xs text-muted-foreground">
          {checked > 0 ? "yield" : "no checks"}
        </span>
      </div>
      {checked > 0 ? (
        <div className="flex h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-emerald-500"
            style={{ width: `${goodShare}%` }}
          />
          <div
            className="h-full bg-rose-500"
            style={{ width: `${badShare}%` }}
          />
        </div>
      ) : (
        <div className="h-1 w-full rounded-full bg-muted" />
      )}
      <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground tabular-nums">
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-emerald-500" />
          <span className="font-medium text-foreground">{good}</span> good
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-rose-500" />
          <span className="font-medium text-foreground">{bad}</span> bad
        </span>
      </div>
    </div>
  );
};

const Cassettes: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<CassetteSortKey>("lastUsedAt");
  const [sortDirection, setSortDirection] =
    useState<CassetteSortDirection>("DESC");
  const [pageCursors, setPageCursors] = useState<Map<number, string | null>>(
    new Map([[0, null]])
  );

  const { data, loading, error } = useCassettesQuery({
    variables: {
      after: pageCursors.get(page) ?? null,
    },
    fetchPolicy: "cache-and-network",
  });

  const cassettes = data?.cassettes?.nodes ?? [];
  const pageInfo = data?.cassettes?.pageInfo;
  const hasNextPage = Boolean(pageInfo?.hasNextPage);
  const nextCursor = pageInfo?.endCursor ?? null;

  const visibleCassettes = useMemo(
    () =>
      cassettes.filter(
        (cassette): cassette is NonNullable<(typeof cassettes)[number]> =>
          Boolean(cassette?.serialNumber)
      ),
    [cassettes]
  );

  const displayedCassettes = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    const filtered = normalizedSearch.length
      ? visibleCassettes.filter((cassette) =>
          formatCassetteLabel(cassette).toLowerCase().includes(normalizedSearch)
        )
      : visibleCassettes;

    return [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortKey) {
        case "remainingCustomerTests":
          comparison =
            (a.remainingCustomerTests ?? 0) - (b.remainingCustomerTests ?? 0);
          break;
        case "remainingPhysicalSensors":
          comparison =
            (a.remainingPhysicalSensors ?? 0) -
            (b.remainingPhysicalSensors ?? 0);
          break;
        case "lastUsedAt":
        default: {
          const aUsedAt = a.lastUsedBy?.usedAt
            ? new Date(a.lastUsedBy.usedAt).getTime()
            : 0;
          const bUsedAt = b.lastUsedBy?.usedAt
            ? new Date(b.lastUsedBy.usedAt).getTime()
            : 0;
          comparison = aUsedAt - bUsedAt;
          break;
        }
      }

      if (comparison === 0) {
        comparison = formatCassetteLabel(a).localeCompare(
          formatCassetteLabel(b)
        );
      }

      return sortDirection === "ASC" ? comparison : -comparison;
    });
  }, [searchQuery, sortDirection, sortKey, visibleCassettes]);

  const handleSort = (key: CassetteSortKey) => {
    if (sortKey === key) {
      setSortDirection((currentDirection) =>
        currentDirection === "ASC" ? "DESC" : "ASC"
      );
      return;
    }

    setSortKey(key);
    setSortDirection(key === "lastUsedAt" ? "DESC" : "DESC");
  };

  const renderSortIcon = (key: CassetteSortKey) => {
    if (sortKey !== key) {
      return <ArrowUpDown className="size-4 text-muted-foreground" />;
    }

    return sortDirection === "ASC" ? (
      <ArrowUp className="size-4 text-foreground" />
    ) : (
      <ArrowDown className="size-4 text-foreground" />
    );
  };

  useEffect(() => {
    if (loading || !hasNextPage || !nextCursor) {
      return;
    }

    setPageCursors((previous) => {
      if (previous.get(page + 1) === nextCursor) {
        return previous;
      }

      const next = new Map(previous);
      next.set(page + 1, nextCursor);
      return next;
    });
  }, [hasNextPage, loading, nextCursor, page]);

  const listSummary = data?.cassetteListSummary;

  return (
    <PageTemplate
      title="Cassettes"
      activeItem="Admin"
      activeSubItem="Cassettes"
      breadcrumbs={[
        { label: "Admin", path: Routes.Admin },
        { label: "Cassettes", path: Routes.AdminCassettes },
      ]}
    >
      <div className="flex flex-col gap-6">
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Provisioned cassettes</CardDescription>
              <CardTitle className="text-2xl">
                {loading && !listSummary
                  ? "..."
                  : listSummary?.provisionedCount ?? 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Allocated tests</CardDescription>
              <CardTitle className="text-2xl">
                {listSummary?.allocatedCustomerTests ?? 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Remaining tests</CardDescription>
              <CardTitle className="text-2xl">
                {listSummary?.remainingCustomerTests ?? 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Open overflow warnings</CardDescription>
              <CardTitle className="text-2xl">
                {listSummary?.openOverflowWarningCount ?? 0}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Cassette list</CardTitle>
            <CardDescription>
              Operational view of purchased tests, remaining tests, physical
              capacity, and overflow warnings.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 w-full md:max-w-sm">
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by serial number"
              />
            </div>
            {error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                Failed to load cassettes: {error.message}
              </div>
            ) : loading && displayedCassettes.length === 0 ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : displayedCassettes.length === 0 ? (
              <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
                No provisioned cassettes match the current search.
              </div>
            ) : (
              <div className="space-y-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cassette</TableHead>
                      <TableHead
                        className="cursor-pointer"
                        onClick={() => handleSort("lastUsedAt")}
                      >
                        <div className="flex items-center gap-2">
                          <span>Last used by</span>
                          {renderSortIcon("lastUsedAt")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer"
                        onClick={() => handleSort("remainingCustomerTests")}
                      >
                        <div className="flex items-center gap-2">
                          <span>Customer tests</span>
                          {renderSortIcon("remainingCustomerTests")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer"
                        onClick={() => handleSort("remainingPhysicalSensors")}
                      >
                        <div className="flex items-center gap-2">
                          <span>Physical sensors</span>
                          {renderSortIcon("remainingPhysicalSensors")}
                        </div>
                      </TableHead>
                      <TableHead>Yield</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedCassettes.map((cassette) => {
                      const cassetteHref = getRoute(
                        Routes.AdminCassetteDetails,
                        {
                          id: cassette.id,
                        }
                      );
                      const cassetteSerialNumber =
                        formatCassetteSerialNumber(cassette);

                      return (
                        <TableRow
                          key={cassette.id}
                          className="group cursor-pointer transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
                          role="link"
                          tabIndex={0}
                          onClick={() => navigate(cassetteHref)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              navigate(cassetteHref);
                            }
                          }}
                        >
                          <TableCell className="py-3">
                            <div className="flex items-center gap-1">
                              <span className="font-medium underline-offset-4 group-hover:underline">
                                {formatCassetteLabel(cassette)}
                              </span>
                              {cassetteSerialNumber ? (
                                <CopyButton
                                  value={cassetteSerialNumber}
                                  label="serial number"
                                />
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell className="py-3 align-top">
                            <div className="min-w-[190px] space-y-0.5">
                              <div className="text-sm font-medium text-foreground">
                                {displayUserName(
                                  cassette.lastUsedBy,
                                  "Never used"
                                )}
                              </div>
                              {cassette.lastUsedBy?.email &&
                              cassette.lastUsedBy.fullName?.trim() &&
                              !isDeletedUserEmail(cassette.lastUsedBy.email) ? (
                                <div className="text-[11px] text-muted-foreground">
                                  {cassette.lastUsedBy.email}
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell className="py-3 align-top">
                            <UsageMeterCell
                              total={cassette.allocatedCustomerTests ?? 0}
                              used={cassette.usedCustomerTests ?? 0}
                              remaining={cassette.remainingCustomerTests ?? 0}
                            />
                          </TableCell>
                          <TableCell className="py-3 align-top">
                            <UsageMeterCell
                              total={
                                (cassette.physicalSensorsUsed ?? 0) +
                                (cassette.remainingPhysicalSensors ?? 0)
                              }
                              used={cassette.physicalSensorsUsed ?? 0}
                              remaining={cassette.remainingPhysicalSensors ?? 0}
                            />
                          </TableCell>
                          <TableCell className="py-3 align-top">
                            <YieldCell
                              good={cassette.goodSensorCount ?? 0}
                              bad={cassette.badSensorCount ?? 0}
                            />
                          </TableCell>
                          <TableCell className="py-3">
                            <div className="flex max-w-[200px] flex-wrap gap-1.5">
                              {cassette.hasOpenOverflowWarning ? (
                                <Badge variant="destructive">
                                  Overflow warning
                                </Badge>
                              ) : null}
                              {(cassette.remainingCustomerTests ?? 0) <= 0 ? (
                                <Badge variant="secondary">
                                  No tests remaining
                                </Badge>
                              ) : null}
                              {(cassette.remainingPhysicalSensors ?? 0) <= 0 ? (
                                <Badge variant="secondary">
                                  No physical sensors remaining
                                </Badge>
                              ) : null}
                              {!cassette.hasOpenOverflowWarning &&
                              (cassette.remainingCustomerTests ?? 0) > 0 &&
                              (cassette.remainingPhysicalSensors ?? 0) > 0 ? (
                                <Badge variant="secondary">Active</Badge>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {(page > 0 || hasNextPage) && (
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            if (page > 0) {
                              setPage((currentPage) => currentPage - 1);
                            }
                          }}
                          className={
                            page === 0
                              ? "pointer-events-none opacity-50"
                              : undefined
                          }
                          aria-disabled={page === 0}
                        />
                      </PaginationItem>
                      <PaginationItem>
                        <span className="px-3 text-sm text-muted-foreground">
                          Page {page + 1}
                        </span>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            if (hasNextPage) {
                              setPage((currentPage) => currentPage + 1);
                            }
                          }}
                          className={
                            !hasNextPage
                              ? "pointer-events-none opacity-50"
                              : undefined
                          }
                          aria-disabled={!hasNextPage}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageTemplate>
  );
};

export default Cassettes;
